import type { FolderListingLayout } from '@/lib/api/content-route-index.server';
import type { AppConfig } from '@/types/config';

type ContentHitsConfig = AppConfig['search']['content']['hits'];
type ContentSkeletonsConfig = AppConfig['search']['content']['skeletons'];

const DEFAULT_CRITICAL = 3;
const DEFAULT_CRITICAL_SQUARED = 4;
const DEFAULT_SKELETONS_DESKTOP = 6;
const DEFAULT_SKELETONS_MOBILE = 3;
const DEFAULT_SKELETONS_SQUARED_DESKTOP = 8;
const DEFAULT_SKELETONS_SQUARED_MOBILE = 4;

export function isSquaredFolderListingLayout(layout: FolderListingLayout | null | undefined): boolean {
    return layout === 'squared-card-layout';
}

/** Critical SSR hit count for the folder listing layout (one full desktop row). */
export function getContentFolderCriticalCount(
    hits: ContentHitsConfig,
    layout: FolderListingLayout | null | undefined
): number {
    if (isSquaredFolderListingLayout(layout)) {
        return hits.criticalSquared ?? DEFAULT_CRITICAL_SQUARED;
    }
    return hits.critical ?? DEFAULT_CRITICAL;
}

/**
 * Non-critical skeleton cap for the current viewport and folder listing layout.
 * Pair with `useMediaQuery('(min-width: 1024px)')`.
 */
export function getContentFolderMaxNonCriticalSkeletonCount(
    skeletons: ContentSkeletonsConfig,
    isDesktop: boolean,
    layout: FolderListingLayout | null | undefined
): number {
    const isSquared = isSquaredFolderListingLayout(layout);
    if (isDesktop) {
        return isSquared
            ? (skeletons?.squaredDesktop ?? DEFAULT_SKELETONS_SQUARED_DESKTOP)
            : (skeletons?.desktop ?? DEFAULT_SKELETONS_DESKTOP);
    }
    return isSquared
        ? (skeletons?.squaredMobile ?? DEFAULT_SKELETONS_SQUARED_MOBILE)
        : (skeletons?.mobile ?? DEFAULT_SKELETONS_MOBILE);
}

/** Minimum tiles while the whole listing is navigating/loading. */
export function getContentFolderMinLoadingSkeletonCount(layout: FolderListingLayout | null | undefined): number {
    return isSquaredFolderListingLayout(layout) ? DEFAULT_SKELETONS_SQUARED_DESKTOP : DEFAULT_SKELETONS_DESKTOP;
}
